import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useCustomization } from "@/context/CustomizationContext";
import {
  DEFAULT_STATUS_HEX, defaultStateHex, defaultSpecialtyHex,
} from "@/lib/colorCoding";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Lock, Trash2, Plus, Loader2, RotateCcw, SlidersHorizontal, GripVertical,
  LockOpen, FileCheck2, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TYPE_LABELS = {
  text: "Text", number: "Number", currency: "Currency",
  percentage: "Percentage",
  date: "Date", dropdown: "Dropdown", boolean: "Yes/No",
};

const TABLE_TITLES = {
  business_tracker: "Business Tracker",
  revenue_details: "Revenue Details",
  services: "Services",
};

const SECTION_LABEL_FALLBACKS = {
  business_tracker: "Business Tracker",
  revenue_details: "Revenue Details",
  revenue_summary: "Revenue Summary",
};

const TEMPLATE_LABELS = {
  business_tracker: "Business Tracker",
  revenue_details: "Revenue Details",
  services: "Services",
};

const TEMPLATE_DOWNLOADS = {
  business_tracker: {
    endpoint: "/engagements/template",
    filename: "Business_Tracker_Template.xlsx",
  },
  revenue_details: {
    endpoint: "/revenue-details/template",
    filename: "Revenue_Details_Template.xlsx",
  },
  services: {
    endpoint: "/services/template",
    filename: "Services_Template.xlsx",
  },
};

const safeColumns = (columns) => (
  Array.isArray(columns)
    ? columns.filter((column) => column && typeof column === "object").map((column) => ({ ...column }))
    : []
);

const safeSummaryDefinition = (definition) => {
  const source = definition && typeof definition === "object" && !Array.isArray(definition)
    ? definition
    : {};
  return {
    ...source,
    categories: Array.isArray(source.categories)
      ? source.categories.filter((item) => item && typeof item === "object")
      : [],
    metrics: Array.isArray(source.metrics)
      ? source.metrics.filter((item) => item && typeof item === "object")
      : [],
  };
};

const CustomizationAttentionNotice = ({ issues = [], testId = "cp-settings-attention" }) => {
  if (!issues.length) return null;
  const verb = issues.length === 1 ? "is" : "are";

  return (
    <Alert
      className="mb-3 border-amber-200 bg-amber-50 text-amber-950"
      data-testid={testId}
    >
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Workspace settings need attention</AlertTitle>
      <AlertDescription>
        {issues.map((issue) => issue.label).join(", ")} {verb} incomplete. This tab is still
        available with safe defaults; review and save the affected settings when ready.
      </AlertDescription>
    </Alert>
  );
};

/* ------------------------------------------------------------------ */
/* Column manager                                                       */
/* ------------------------------------------------------------------ */

const AddColumnDialog = ({ open, onOpenChange, onAdd, tableKey, tableTitle }) => {
  const [form, setForm] = useState({ label: "", type: "text", decimals: "", options: "" });
  useEffect(() => {
    if (open) setForm({ label: "", type: "text", decimals: "", options: "" });
  }, [open]);

  const submit = () => {
    if (!form.label.trim()) { toast.error("Please give the column a name."); return; }
    const options = form.options.split(",").map((s) => s.trim()).filter(Boolean);
    if (form.type === "dropdown" && !options.length) {
      toast.error("Dropdown columns need at least one option."); return;
    }
    onAdd({
      key: "", label: form.label.trim(), type: form.type, visible: true, core: false,
      financial: form.type === "currency" || form.type === "percentage",
      decimals: form.decimals === "" ? null : parseInt(form.decimals, 10),
      options,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`cp-add-column-dialog-${tableKey}`}>
        <DialogHeader>
          <DialogTitle>Add custom column</DialogTitle>
          <DialogDescription>
            The new column appears on the {tableTitle || TABLE_TITLES[tableKey]} table in this workspace only.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Column name</Label>
            <Input data-testid="cp-new-column-label" placeholder="e.g. Region"
              value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Data format</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="cp-new-column-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(form.type === "number" || form.type === "currency" || form.type === "percentage") && (
              <div className="space-y-1.5">
                <Label>Decimals</Label>
                <Select value={form.decimals === "" ? "default" : String(form.decimals)}
                  onValueChange={(v) => setForm({ ...form, decimals: v === "default" ? "" : v })}>
                  <SelectTrigger data-testid="cp-new-column-decimals"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (2)</SelectItem>
                    {[0, 1, 2, 3, 4].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {form.type === "dropdown" && (
            <div className="space-y-1.5">
              <Label>Options <span className="text-slate-400 font-normal">(comma separated)</span></Label>
              <Input data-testid="cp-new-column-options" placeholder="e.g. East, West, Central"
                value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="cp-new-column-submit" onClick={submit}>Add Column</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Sortable row — one column entry in the ColumnsEditor list */
const SortableColumnRow = ({ id, col, i, tableKey, update, setDeleting, removeLocal }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled: col.locked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: "relative",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-[34px_minmax(180px,220px)_110px_120px_minmax(160px,1fr)_70px_46px] gap-2 items-center px-3 py-1.5 border-b last:border-0 ${isDragging ? "bg-slate-100 shadow-sm rounded" : "hover:bg-slate-50/60"}`}
      data-testid={`cp-column-row-${tableKey}-${col.key || i}`}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className={`flex items-center justify-center text-slate-300 transition-colors ${
          col.locked ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing hover:text-slate-500"
        }`}
        title={col.locked ? "Unlock this column to reorder it" : "Drag to reorder"}
        data-testid={`cp-col-drag-${tableKey}-${col.key || i}`}
      >
        <GripVertical className="h-4 w-4" />
      </span>

      {/* Label */}
      <div className="flex min-w-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={`h-7 w-7 shrink-0 ${
            col.locked ? "text-slate-500" : "text-teal-700 hover:text-teal-800"
          }`}
          data-testid={`cp-col-lock-${tableKey}-${col.key || i}`}
          aria-label={`${col.locked ? "Unlock" : "Lock"} ${col.label} column`}
          title={col.locked ? "Unlock column settings" : "Lock column settings"}
          onClick={() => update(i, { locked: !col.locked })}
        >
          {col.locked
            ? <Lock className="h-3.5 w-3.5" />
            : <LockOpen className="h-3.5 w-3.5" />}
        </Button>
        <Input className="h-7 text-xs" value={col.label} disabled={col.locked}
          data-testid={`cp-col-label-${tableKey}-${col.key || i}`}
          onChange={(e) => update(i, { label: e.target.value })} />
      </div>

      {/* Type badge */}
      <Badge variant="outline" className="justify-center text-[10px] font-medium text-slate-600">
        {TYPE_LABELS[col.type] || col.type}
      </Badge>

      {/* Decimals */}
      {(col.type === "number" || col.type === "currency" || col.type === "percentage") ? (
        <Select value={col.decimals == null ? "default" : String(col.decimals)}
          disabled={col.locked}
          onValueChange={(v) => update(i, { decimals: v === "default" ? null : parseInt(v, 10) })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`cp-col-decimals-${tableKey}-${col.key || i}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            {[0, 1, 2, 3, 4].map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : <span className="text-xs text-slate-300 text-center">—</span>}

      {/* Options */}
      {col.type === "dropdown" ? (
        <Input className="h-7 text-xs" value={(col.options || []).join(", ")}
          disabled={col.locked}
          data-testid={`cp-col-options-${tableKey}-${col.key || i}`}
          placeholder="Option A, Option B"
          onChange={(e) => update(i, { options: e.target.value.split(",").map((s) => s.trimStart()) })} />
      ) : <span className="text-xs text-slate-300">—</span>}

      {/* Visible toggle */}
      <span className="flex justify-center">
        <Switch checked={col.visible !== false}
          disabled={col.locked}
          data-testid={`cp-col-visible-${tableKey}-${col.key || i}`}
          onCheckedChange={(v) => update(i, { visible: v })} />
      </span>

      {/* Delete */}
      <span className="flex justify-center">
        {!col.locked && (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700"
            data-testid={`cp-col-delete-${tableKey}-${col.key || i}`}
            onClick={() => (col.key ? setDeleting(col) : removeLocal(i))}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </span>
    </div>
  );
};

const ColumnsEditor = ({ tableKey }) => {
  const { configs, sectionLabels, incompleteSettings, refresh } = useCustomization();
  const [cols, setCols] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [verifyingTemplate, setVerifyingTemplate] = useState(false);
  const [templateVerification, setTemplateVerification] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (configs) {
      setCols(safeColumns(configs[tableKey]?.columns));
      setDirty(false);
    }
  }, [configs, tableKey]);

  if (!cols) return <p className="text-sm text-slate-500 py-8">Loading…</p>;

  const tableTitle = sectionLabels?.[tableKey] || TABLE_TITLES[tableKey];
  const templateLabel = sectionLabels?.[tableKey] || TEMPLATE_LABELS[tableKey];
  const summaryTitle = sectionLabels?.revenue_summary || SECTION_LABEL_FALLBACKS.revenue_summary;

  const update = (i, patch) => {
    setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    setDirty(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id, 10);
      const newIndex = parseInt(over.id, 10);
      setCols((prev) => arrayMove(prev, oldIndex, newIndex));
      setDirty(true);
    }
  };

  const addColumn = (col) => { setCols((prev) => [...prev, col]); setDirty(true); };
  const removeLocal = (i) => { setCols((prev) => prev.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/table-configs/${tableKey}`, { columns: cols });
      await refresh();
      setTemplateVerification(null);
      toast.success("Table layout saved for this workspace.");
      const cleanup = res.data?.summary_cleanup;
      if (cleanup && (cleanup.removed_categories?.length || cleanup.removed_metrics?.length)) {
        const removed = [
          cleanup.removed_categories?.length
            ? `categories: ${cleanup.removed_categories.join(", ")}`
            : null,
          cleanup.removed_metrics?.length
            ? `metrics: ${cleanup.removed_metrics.join(", ")}`
            : null,
        ].filter(Boolean).join(" · ");
        toast.warning(`${summaryTitle} definitions removed because their source fields are unavailable — ${removed}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save the table layout."));
    } finally {
      setSaving(false);
    }
  };

  const verifyTemplate = async () => {
    if (dirty) {
      toast.warning("Save your table layout changes before verifying the import template.");
      return;
    }
    setVerifyingTemplate(true);
    try {
      const template = TEMPLATE_DOWNLOADS[tableKey];
      const res = await api.get(template.endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${templateLabel.replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "")}_Template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setTemplateVerification({ opened: true });
      toast.success(
        `${templateLabel} template downloaded. Open it to review before importing.`
      );
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not open the import template."));
    } finally {
      setVerifyingTemplate(false);
    }
  };

  const confirmDelete = async () => {
    const col = deleting;
    setDeleting(null);
    try {
      // A lock toggle is part of the local layout draft. Persist it before
      // deleting so the API evaluates the same unlocked state the user saw.
      if (dirty) {
        await api.put(`/table-configs/${tableKey}`, { columns: cols });
        setDirty(false);
      }
      const res = await api.delete(`/table-configs/${tableKey}/columns/${col.key}`);
      await refresh();
      toast.success(`Deleted column "${col.label}" (cleared ${res.data.cleared_rows} row values).`);
      const cleanup = res.data?.summary_cleanup;
      if (cleanup && (cleanup.removed_categories?.length || cleanup.removed_metrics?.length)) {
        const removed = [
          cleanup.removed_categories?.length
            ? `categories: ${cleanup.removed_categories.join(", ")}`
            : null,
          cleanup.removed_metrics?.length
            ? `metrics: ${cleanup.removed_metrics.join(", ")}`
            : null,
        ].filter(Boolean).join(" · ");
        toast.warning(`${summaryTitle} definitions removed because their source fields are unavailable — ${removed}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete the column."));
    }
  };

  // IDs are simply the array indices — stable within a single drag gesture
  const sortableIds = cols.map((_, i) => String(i));

  return (
    <div data-testid={`cp-columns-editor-${tableKey}`}>
      <CustomizationAttentionNotice
        issues={(incompleteSettings || []).filter((issue) => issue.key === tableKey)}
        testId={`cp-settings-attention-${tableKey}`}
      />
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">
          Rename, reorder, hide or format columns. Use the lock toggle before each Header Label
          to enable or disable its settings. Core fields (<Lock className="inline h-3 w-3 -mt-0.5" />)
          power calculations and cannot be deleted. Drag <GripVertical className="inline h-3 w-3 -mt-0.5 text-slate-400" /> to reorder unlocked columns.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-lg"
            data-testid={`cp-verify-template-btn-${tableKey}`}
            disabled={verifyingTemplate || dirty} onClick={verifyTemplate}>
            {verifyingTemplate
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <FileCheck2 className="h-3.5 w-3.5 mr-1" />}
            Verify Import Template
          </Button>
          <Button size="sm" variant="outline" className="rounded-lg" data-testid={`cp-add-column-btn-${tableKey}`}
            onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Column
          </Button>
          <Button size="sm" className="rounded-lg" data-testid={`cp-save-btn-${tableKey}`}
            disabled={!dirty || saving} onClick={save}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Changes"}
          </Button>
        </div>
      </div>
      {templateVerification && (
        <div
          className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
          data-testid={`cp-template-verification-${tableKey}`}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>
            Updated Import Template downloaded. Review the actual sheet; this is the same
            template available from the import section.
          </span>
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[34px_minmax(180px,220px)_110px_120px_minmax(160px,1fr)_70px_46px] gap-2 items-center px-3 py-2 border-b bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <span></span>
          <span>Header Label</span><span>Format</span><span>Decimals</span>
          <span>Options</span><span>Visible</span><span></span>
        </div>
        {cols.length === 0 && (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid={`cp-empty-columns-${tableKey}`}>
            No columns configured yet.
          </p>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {cols.map((c, i) => (
              <SortableColumnRow
                key={c.key || `new-${i}`}
                id={String(i)}
                col={c}
                i={i}
                tableKey={tableKey}
                update={update}
                setDeleting={setDeleting}
                removeLocal={removeLocal}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <AddColumnDialog open={addOpen} onOpenChange={setAddOpen} onAdd={addColumn}
        tableKey={tableKey} tableTitle={tableTitle} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="cp-delete-column-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete column "{deleting?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the column from the {TABLE_TITLES[tableKey]} table and permanently clears
              its values on every row in this workspace. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="cp-delete-column-confirm-btn"
              onClick={confirmDelete}>
              Delete Column
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Revenue Summary definition                                           */
/* ------------------------------------------------------------------ */

const summaryKey = (label) => String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);

const SectionNamesEditor = () => {
  const { sectionLabels, refresh } = useCustomization();
  const [labels, setLabels] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sectionLabels) {
      setLabels({ ...SECTION_LABEL_FALLBACKS, ...sectionLabels });
      setDirty(false);
    }
  }, [sectionLabels]);

  if (!labels) return <p className="text-sm text-slate-500 py-8">Loading…</p>;

  const update = (key, value) => {
    setLabels((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    const clean = Object.fromEntries(
      Object.entries(labels).map(([key, value]) => [key, value.trim()])
    );
    if (Object.values(clean).some((value) => !value)) {
      toast.error("Each section needs a name.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/section-labels", { labels: clean });
      await refresh();
      toast.success("Workspace section names saved.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save the section names."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cp-section-names-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">
          Name the three client-facing sections for this workspace. These names update the navigation,
          page titles, imports, and related messages for this workspace only.
        </p>
        <Button size="sm" className="rounded-lg" data-testid="cp-save-section-names"
          disabled={!dirty || saving} onClick={save}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Names"}
        </Button>
        {/* Future workspace sections can be added here once their destination page/content is defined.
        <Button variant="outline" size="sm" className="rounded-lg" data-testid="cp-add-section">
          Add Section
        </Button>
        */}
      </div>
      <div className="max-w-2xl rounded-xl border bg-white p-4 space-y-3">
        {Object.entries(SECTION_LABEL_FALLBACKS).map(([key, fallback]) => (
          <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 items-center">
            <Label className="text-sm text-slate-600">{fallback}</Label>
            <Input
              className="h-8 text-sm"
              value={labels[key] || ""}
              maxLength={60}
              data-testid={`cp-section-label-${key}`}
              onChange={(e) => update(key, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const SummaryLabelsEditor = () => {
  const { configs, getColumns, sectionLabels, incompleteSettings, refresh } = useCustomization();
  const summaryConfig = configs?.revenue_summary?.config;
  const detailColumns = getColumns("revenue_details") || [];
  const summaryTitle = sectionLabels?.revenue_summary || SECTION_LABEL_FALLBACKS.revenue_summary;
  const detailsTitle = sectionLabels?.revenue_details || SECTION_LABEL_FALLBACKS.revenue_details;
  const [definition, setDefinition] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState({ label: "", field: "classification", operator: "contains", value: "" });
  const [newMetric, setNewMetric] = useState({ label: "", field: "" });

  useEffect(() => {
    if (summaryConfig && typeof summaryConfig === "object") {
      setDefinition(safeSummaryDefinition(summaryConfig));
      setDirty(false);
    }
  }, [summaryConfig]);

  if (!definition) return <p className="text-sm text-slate-500 py-8">Loading…</p>;

  const update = (group, index, value) => {
    setDefinition((prev) => ({
      ...prev,
      [group]: prev[group].map((item, i) => i === index ? { ...item, ...value } : item),
    }));
    setDirty(true);
  };

  const remove = (group, index) => {
    if (definition[group][index].protected) {
      toast.error("Core summary definitions are protected.");
      return;
    }
    setDefinition((prev) => ({ ...prev, [group]: prev[group].filter((_, i) => i !== index) }));
    setDirty(true);
  };

  const addCategory = () => {
    const label = newCategory.label.trim();
    const value = newCategory.value.trim();
    const key = summaryKey(label);
    if (!label || !value || !key) { toast.error("Give the category a name and matching value."); return; }
    if (definition.categories.some((item) => item.key === key)) { toast.error("That category key already exists."); return; }
    setDefinition((prev) => ({
      ...prev,
      categories: [...prev.categories, { key, label, ...newCategory, value, protected: false }],
    }));
    setNewCategory({ label: "", field: "classification", operator: "contains", value: "" });
    setDirty(true);
  };

  const addMetric = () => {
    const label = newMetric.label.trim();
    const key = summaryKey(label);
    const column = detailColumns.find((item) => item.key === newMetric.field);
    if (!label || !key || !column) { toast.error("Give the metric a name and choose a numeric field."); return; }
    if (definition.metrics.some((item) => item.key === key)) { toast.error("That metric key already exists."); return; }
    setDefinition((prev) => ({
      ...prev,
      metrics: [...prev.metrics, {
        key, label, field: column.key, type: column.type,
        financial: Boolean(column.financial), protected: false,
      }],
    }));
    setNewMetric({ label: "", field: "" });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/table-configs/revenue_summary", definition);
      await refresh();
      toast.success(`${summaryTitle} definition saved.`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save the labels."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cp-summary-labels-editor">
      <CustomizationAttentionNotice
        issues={(incompleteSettings || []).filter((issue) => issue.key === "revenue_summary")}
        testId="cp-settings-attention-revenue_summary"
      />
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">
           Configure which business categories and numeric metrics appear in this workspace's {summaryTitle}.
        </p>
        <Button size="sm" className="rounded-lg" data-testid="cp-save-summary-labels"
          disabled={!dirty || saving} onClick={save}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Changes"}
        </Button>
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-800 mb-1">Business Categories</h4>
          <p className="text-[11px] text-slate-500 mb-3">A detail is assigned to the first matching category.</p>
          <div className="space-y-2">
            {definition.categories.map((item, index) => (
              <div key={item.key} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Input className="h-8 text-sm" value={item.label} data-testid={`cp-summary-cat-${item.key}`}
                  onChange={(e) => update("categories", index, { label: e.target.value })} />
                <Select value={item.field} onValueChange={(v) => update("categories", index, { field: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classification">Built-in classification</SelectItem>
                    {detailColumns.filter((column) => ["text", "dropdown"].includes(column.type)).map((column) =>
                      <SelectItem key={column.key} value={column.key}>{column.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="h-8 text-sm" value={item.value} placeholder="Matches…"
                  onChange={(e) => update("categories", index, { value: e.target.value })} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400" disabled={item.protected}
                  onClick={() => remove("categories", index)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
            <Input className="h-8 text-sm" placeholder="New category" value={newCategory.label}
              onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })} />
            <Select value={newCategory.field} onValueChange={(v) => setNewCategory({ ...newCategory, field: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="classification">Built-in classification</SelectItem>
                {detailColumns.filter((column) => ["text", "dropdown"].includes(column.type)).map((column) =>
                  <SelectItem key={column.key} value={column.key}>{column.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="h-8 text-sm" placeholder="Matching value" value={newCategory.value}
              onChange={(e) => setNewCategory({ ...newCategory, value: e.target.value })} />
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={addCategory}><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-800 mb-1">Summary Metrics</h4>
          <p className="text-[11px] text-slate-500 mb-3">Metrics are sums of numeric {detailsTitle} fields. Financial fields stay protected.</p>
          <div className="space-y-2">
            {definition.metrics.map((item, index) => (
              <div key={item.key} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <Input className="h-8 text-sm" value={item.label} data-testid={`cp-summary-metric-${item.key}`}
                  onChange={(e) => update("metrics", index, { label: e.target.value })} />
                <span className="h-8 rounded-md border bg-slate-50 px-2 flex items-center text-xs text-slate-500 truncate">
                  {detailColumns.find((column) => column.key === item.field)?.label || item.field}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400" disabled={item.protected}
                  onClick={() => remove("metrics", index)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <Input className="h-8 text-sm" placeholder="New metric" value={newMetric.label}
              onChange={(e) => setNewMetric({ ...newMetric, label: e.target.value })} />
            <Select value={newMetric.field} onValueChange={(v) => setNewMetric({ ...newMetric, field: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Numeric field" /></SelectTrigger>
              <SelectContent>
                {detailColumns.filter((column) => ["number", "currency", "percentage"].includes(column.type)).map((column) =>
                  <SelectItem key={column.key} value={column.key}>{column.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={addMetric}><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Colors                                                               */
/* ------------------------------------------------------------------ */

const ColorRow = ({ name, override, fallback, onChange, onReset, testId }) => (
  <div className="flex items-center gap-3 px-3 py-1.5 border-b last:border-0 hover:bg-slate-50/60">
    <input type="color" value={override || fallback}
      data-testid={`${testId}-picker`}
      className="h-7 w-10 rounded border cursor-pointer bg-white p-0.5"
      onChange={(e) => onChange(e.target.value)} />
    <span className="text-sm text-slate-700 flex-1 truncate" title={name}>{name}</span>
    {override ? (
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-slate-500"
        data-testid={`${testId}-reset`} onClick={onReset}>
        <RotateCcw className="h-3 w-3 mr-1" /> Auto
      </Button>
    ) : (
      <Badge variant="outline" className="text-[10px] text-slate-400 font-normal">Auto</Badge>
    )}
  </div>
);

const ColorsEditor = () => {
  const { colors, refresh, getColumns, sectionLabels, incompleteSettings } = useCustomization();
  const [meta, setMeta] = useState(null);
  const [local, setLocal] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => setMeta({}));
  }, []);
  useEffect(() => {
    setLocal(JSON.parse(JSON.stringify(colors)));
    setDirty(false);
  }, [colors]);

  const statusOptions = useMemo(() => {
    const fromConfig = (getColumns("business_tracker") || []).find((c) => c.key === "status")?.options || [];
    return Array.from(new Set([...(meta?.statuses || []), ...fromConfig]));
  }, [meta, getColumns]);

  if (!meta || !local) return <p className="text-sm text-slate-500 py-8">Loading…</p>;

  const setColor = (group, key, value) => {
    setLocal((prev) => ({ ...prev, [group]: { ...prev[group], [key]: value } }));
    setDirty(true);
  };
  const resetColor = (group, key) => {
    setLocal((prev) => {
      const next = { ...prev, [group]: { ...prev[group] } };
      delete next[group][key];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/color-settings", local);
      await refresh();
      toast.success("Workspace colors saved.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save the colors."));
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    {
      key: "status_colors", title: "Status Colors", subtitle: `Row tint & badge color in ${sectionLabels?.business_tracker || SECTION_LABEL_FALLBACKS.business_tracker}`,
      items: statusOptions, fallback: (v) => DEFAULT_STATUS_HEX[v] || "#64748b", testPrefix: "cp-color-status",
    },
    {
      key: "state_colors", title: "State Colors", subtitle: "Badges & dashboard state chart",
      items: meta.states || [], fallback: defaultStateHex, testPrefix: "cp-color-state",
    },
    {
      key: "specialty_colors", title: "Specialty Colors", subtitle: "Badges & dashboard specialty chart",
      items: meta.specialties || [], fallback: defaultSpecialtyHex, testPrefix: "cp-color-specialty",
    },
  ];

  return (
    <div data-testid="cp-colors-editor">
      <CustomizationAttentionNotice
        issues={(incompleteSettings || []).filter((issue) => issue.key.endsWith("_colors"))}
        testId="cp-settings-attention-colors"
      />
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">
          Pick custom colors per value, or leave on Auto to use the built-in palette. Applies to this workspace only.
        </p>
        <Button size="sm" className="rounded-lg" data-testid="cp-save-colors"
          disabled={!dirty || saving} onClick={save}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save Colors"}
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {sections.map((s) => (
          <div key={s.key} className="rounded-xl border bg-white overflow-hidden h-fit">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h4 className="text-sm font-semibold text-slate-800">{s.title}</h4>
              <p className="text-[11px] text-slate-500">{s.subtitle}</p>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {s.items.length === 0 && (
                <p className="text-xs text-slate-400 px-4 py-6">No values in this workspace yet.</p>
              )}
              {s.items.map((name) => (
                <ColorRow key={name} name={name}
                  override={local[s.key]?.[name]}
                  fallback={s.fallback(name)}
                  testId={`${s.testPrefix}-${name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                  onChange={(v) => setColor(s.key, name, v)}
                  onReset={() => resetColor(s.key, name)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function ControlPanelPage() {
  const { current } = useWorkspace();
  const { sectionLabels, incompleteSettings } = useCustomization();
  const businessTrackerTitle = sectionLabels?.business_tracker || SECTION_LABEL_FALLBACKS.business_tracker;
  const revenueDetailsTitle = sectionLabels?.revenue_details || SECTION_LABEL_FALLBACKS.revenue_details;
  const revenueSummaryTitle = sectionLabels?.revenue_summary || SECTION_LABEL_FALLBACKS.revenue_summary;

  return (
    <div className="space-y-4" data-testid="control-panel-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center">
          <SlidersHorizontal className="h-5 w-5 text-[hsl(var(--primary))]" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Control Panel</h1>
          <p className="text-xs text-slate-500">
            Customize tables and colors for the <span className="font-medium text-slate-700">{current?.name}</span> workspace.
            Each workspace keeps its own layout, so every team can tailor the app to their needs.
          </p>
        </div>
      </div>
      <CustomizationAttentionNotice issues={incompleteSettings} />

      <Tabs defaultValue="section_labels">
        <TabsList className="rounded-xl" data-testid="cp-tabs">
          <TabsTrigger value="section_labels" data-testid="cp-tab-section-names">Section Names</TabsTrigger>
          <TabsTrigger value="services" data-testid="cp-tab-services">Services</TabsTrigger>
          <TabsTrigger value="business_tracker" data-testid="cp-tab-business-tracker">{businessTrackerTitle}</TabsTrigger>
          <TabsTrigger value="revenue_details" data-testid="cp-tab-revenue-details">{revenueDetailsTitle}</TabsTrigger>
          <TabsTrigger value="revenue_summary" data-testid="cp-tab-summary-labels">{revenueSummaryTitle}</TabsTrigger>
          <TabsTrigger value="colors" data-testid="cp-tab-colors">Colors</TabsTrigger>
        </TabsList>
        <TabsContent value="section_labels" className="mt-4">
          <SectionNamesEditor />
        </TabsContent>
        <TabsContent value="business_tracker" className="mt-4">
          <ColumnsEditor tableKey="business_tracker" />
        </TabsContent>
        <TabsContent value="revenue_details" className="mt-4">
          <ColumnsEditor tableKey="revenue_details" />
        </TabsContent>
        <TabsContent value="services" className="mt-4">
          <ColumnsEditor tableKey="services" />
        </TabsContent>
        <TabsContent value="revenue_summary" className="mt-4">
          <SummaryLabelsEditor />
        </TabsContent>
        <TabsContent value="colors" className="mt-4">
          <ColorsEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
